import { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { z } from "zod";

export type ViewportSize = "mobile" | "tablet" | "desktop" | "ultrawide";
export type NavigationMode = "drawer" | "sidebar" | "tabs" | "dropdown";

export interface ViewportConfig {
  size: ViewportSize;
  width: number;
  height: number;
  navigationMode: NavigationMode;
  showBreadcrumbs: boolean;
  compactMode: boolean;
  gestureNavigation: boolean;
}

export interface NavigationState {
  isDrawerOpen: boolean;
  activeTab: string | null;
  collapsedSections: Set<string>;
  history: string[];
  preferences: {
    persistentSidebar: boolean;
    autoCollapse: boolean;
    showTooltips: boolean;
  };
}

/**
 * Multi-viewport navigation coordination hook
 */
export function useMultiViewportNavigation() {
  const location = useLocation();
  const [viewportSize, setViewportSize] = useState<ViewportSize>("desktop");
  const [navigationState, setNavigationState] = useState<NavigationState>({
    isDrawerOpen: false,
    activeTab: null,
    collapsedSections: new Set(),
    history: [],
    preferences: {
      persistentSidebar: true,
      autoCollapse: false,
      showTooltips: true,
    },
  });

  /**
   * Determine viewport size based on window dimensions
   */
  const determineViewportSize = useCallback(
    (width: number, height: number): ViewportSize => {
      if (width < 768) return "mobile";
      if (width < 1024) return "tablet";
      if (width < 1920) return "desktop";
      return "ultrawide";
    },
    [],
  );

  /**
   * Get viewport configuration based on current size
   */
  const viewportConfig = useMemo((): ViewportConfig => {
    const [width, height] = [window.innerWidth, window.innerHeight];

    const configs: Record<
      ViewportSize,
      Omit<ViewportConfig, "size" | "width" | "height">
    > = {
      mobile: {
        navigationMode: "drawer",
        showBreadcrumbs: false,
        compactMode: true,
        gestureNavigation: true,
      },
      tablet: {
        navigationMode: "drawer",
        showBreadcrumbs: true,
        compactMode: true,
        gestureNavigation: true,
      },
      desktop: {
        navigationMode: "sidebar",
        showBreadcrumbs: true,
        compactMode: false,
        gestureNavigation: false,
      },
      ultrawide: {
        navigationMode: "sidebar",
        showBreadcrumbs: true,
        compactMode: false,
        gestureNavigation: false,
      },
    };

    return {
      size: viewportSize,
      width,
      height,
      ...configs[viewportSize],
    };
  }, [viewportSize]);

  /**
   * Update viewport size on window resize
   */
  useEffect(() => {
    const handleResize = () => {
      const newSize = determineViewportSize(
        window.innerWidth,
        window.innerHeight,
      );
      setViewportSize(newSize);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial call

    return () => window.removeEventListener("resize", handleResize);
  }, [determineViewportSize]);

  /**
   * Handle route changes and update navigation state
   */
  useEffect(() => {
    setNavigationState((prev) => ({
      ...prev,
      history: [...prev.history, location.pathname].slice(-10), // Keep last 10 routes
      isDrawerOpen:
        viewportConfig.navigationMode === "drawer" ? false : prev.isDrawerOpen,
    }));
  }, [location.pathname, viewportConfig.navigationMode]);

  /**
   * Toggle navigation drawer
   */
  const toggleDrawer = useCallback(() => {
    setNavigationState((prev) => ({
      ...prev,
      isDrawerOpen: !prev.isDrawerOpen,
    }));
  }, []);

  /**
   * Close navigation drawer
   */
  const closeDrawer = useCallback(() => {
    setNavigationState((prev) => ({
      ...prev,
      isDrawerOpen: false,
    }));
  }, []);

  /**
   * Set active tab for tab navigation mode
   */
  const setActiveTab = useCallback((tab: string | null) => {
    setNavigationState((prev) => ({
      ...prev,
      activeTab: tab,
    }));
  }, []);

  /**
   * Toggle collapsed state of navigation sections
   */
  const toggleSection = useCallback((sectionId: string) => {
    setNavigationState((prev) => {
      const newCollapsed = new Set(prev.collapsedSections);
      if (newCollapsed.has(sectionId)) {
        newCollapsed.delete(sectionId);
      } else {
        newCollapsed.add(sectionId);
      }
      return {
        ...prev,
        collapsedSections: newCollapsed,
      };
    });
  }, []);

  /**
   * Update user preferences
   */
  const updatePreferences = useCallback(
    (updates: Partial<NavigationState["preferences"]>) => {
      setNavigationState((prev) => ({
        ...prev,
        preferences: { ...prev.preferences, ...updates },
      }));
    },
    [],
  );

  /**
   * Get navigation classes for current viewport
   */
  const getNavigationClasses = useCallback(() => {
    const baseClasses = "navigation-container transition-all duration-300";
    const modeClasses = {
      drawer: "fixed inset-y-0 left-0 z-50 transform transition-transform",
      sidebar: "relative flex-shrink-0",
      tabs: "border-b border-border",
      dropdown: "relative",
    };

    const responsiveClasses = {
      mobile: "w-full max-w-sm",
      tablet: "w-80",
      desktop: "w-64",
      ultrawide: "w-72",
    };

    return `${baseClasses} ${modeClasses[viewportConfig.navigationMode]} ${responsiveClasses[viewportSize]}`;
  }, [viewportConfig.navigationMode, viewportSize]);

  /**
   * Handle touch/gesture navigation (for mobile/tablet)
   */
  const handleGestureNavigation = useCallback(
    (gesture: "swipeRight" | "swipeLeft") => {
      if (!viewportConfig.gestureNavigation) return;

      switch (gesture) {
        case "swipeRight":
          if (
            viewportConfig.navigationMode === "drawer" &&
            !navigationState.isDrawerOpen
          ) {
            toggleDrawer();
          }
          break;
        case "swipeLeft":
          if (
            viewportConfig.navigationMode === "drawer" &&
            navigationState.isDrawerOpen
          ) {
            closeDrawer();
          }
          break;
      }
    },
    [viewportConfig, navigationState.isDrawerOpen, toggleDrawer, closeDrawer],
  );

  /**
   * Auto-collapse navigation based on preferences and viewport
   */
useEffect(() => {
    if (!navigationState.preferences.autoCollapse || viewportSize !== "mobile") {
      return;
    }

    const timer = setTimeout(() => {
      closeDrawer();
    }, 3000); // Auto-close after 3 seconds on mobile

    return () => clearTimeout(timer);
  }, [
    location.pathname,
    navigationState.preferences.autoCollapse,
    viewportSize,
    closeDrawer,
  ]);

  /**
   * Persist navigation state in localStorage
   */
  useEffect(() => {
    const persistedState = {
      preferences: navigationState.preferences,
      collapsedSections: Array.from(navigationState.collapsedSections),
    };
    localStorage.setItem("navigation-state", JSON.stringify(persistedState));
  }, [navigationState.preferences, navigationState.collapsedSections]);

  /**
   * Load persisted navigation state
   */
  useEffect(() => {
    try {
      const persistedState = localStorage.getItem("navigation-state");
      if (persistedState) {
        const raw: unknown = JSON.parse(persistedState);
        const result = z.object({
          preferences: z.record(z.string(), z.unknown()).optional(),
          collapsedSections: z.array(z.string()).optional(),
        }).passthrough().safeParse(raw);
        if (result.success) {
          setNavigationState((prev) => ({
            ...prev,
            preferences: { ...prev.preferences, ...result.data.preferences },
            collapsedSections: new Set(result.data.collapsedSections || []),
          }));
        }
      }
    } catch (error) {
      console.warn("Failed to load persisted navigation state:", error);
    }
  }, []);

  /**
   * Check if navigation should be persistent
   */
  const shouldPersistNavigation = useMemo(() => {
    return (
      viewportConfig.navigationMode === "sidebar" &&
      navigationState.preferences.persistentSidebar &&
      viewportSize !== "mobile"
    );
  }, [
    viewportConfig.navigationMode,
    navigationState.preferences.persistentSidebar,
    viewportSize,
  ]);

  return {
    viewportSize,
    viewportConfig,
    navigationState,
    toggleDrawer,
    closeDrawer,
    setActiveTab,
    toggleSection,
    updatePreferences,
    getNavigationClasses,
    handleGestureNavigation,
    shouldPersistNavigation,
  };
}
